import React, { createContext, useContext, useEffect, useState } from "react";

type WindowSize = {
  width: number;
  height: number;
};

type ScreenSizeContextType = {
  windowSize: WindowSize;
  screen: string;
};

const ScreenSizeContext = createContext<ScreenSizeContextType | undefined>(
  undefined
);

interface ScreenSizeProviderProps {
  children: React.ReactNode;
}

export const ScreenSizeProvider: React.FC<ScreenSizeProviderProps> = ({
  children,
}) => {
  const [windowSize, setWindowSize] = useState<WindowSize>({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [screen, setScreen] = useState<string>("xl");

  const returnScreens = (width: number): string => {
    if (width >= 320 && width <= 480) {
      return "sm";
    } else if (width >= 481 && width <= 768) {
      return "md";
    } else if (width >= 769 && width <= 1024) {
      return "lg";
    } else if (width >= 1025 && width <= 1200) {
      return "xl";
    } else {
      return "xxl";
    }
  };

  useEffect(() => {
    const handleResize = (): void => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
      setScreen(returnScreens(window.innerWidth));
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);
  // Conditionally render the screen size display only in a local environment
  const isLocalEnvironment = window.location.hostname === "localhost";
  const showScreenSizeDisplay = isLocalEnvironment;
  return (
    <ScreenSizeContext.Provider value={{ windowSize, screen }}>
      {children}
      {showScreenSizeDisplay && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            right: "8px",
            padding: 16,
            background: "transparent",
            color: "#00B0F5",
          }}
        >
          {screen}
        </div>
      )}
    </ScreenSizeContext.Provider>
  );
};

export const useScreenSize = (): ScreenSizeContextType => {
  const context = useContext(ScreenSizeContext);

  if (!context) {
    throw new Error("useScreenSize must be used within a ScreenSizeProvider");
  }

  return context;
};
